import React, { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Upload,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Building2,
  Image as ImageIcon,
  X,
  Church,
  Beaker,
} from 'lucide-react'
import { DemoModeToggle } from '@/components/DemoModeToggle'
import { supabase } from '@/lib/supabase'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '@/lib/i18n'

interface ChurchSettings {
  currency?: string
  language?: string
  date_format?: string
  country?: string
  fiscal_year_start?: string
  tax_id?: string
  enable_multi_currency?: boolean
  show_usd_equivalent?: boolean
  sms_provider?: string
  email_from_name?: string
  auto_send_receipts?: boolean
  notify_large_donations?: boolean
  large_donation_threshold?: number
  member_number_format?: string
  member_number_prefix?: string
  attendance_grace_period?: number
  enable_qr_checkin?: boolean
}

interface Church {
  id: string
  name: string
  display_name?: string
  denomination?: string
  address?: string
  phone?: string
  email?: string
  website?: string
  pastor_name?: string
  timezone?: string
  logo_url?: string
  banner_url?: string
  founded_date?: string
  settings?: ChurchSettings
}

export default function Settings() {
  const { i18n } = useTranslation()
  const [church, setChurch] = useState<Church | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [bannerPreview, setBannerPreview] = useState<string | null>(null)

  const logoInputRef = useRef<HTMLInputElement>(null)
  const bannerInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadChurchSettings()
  }, [])

  async function loadChurchSettings() {
    try {
      setLoading(true)

      // Get current user's church
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        throw new Error('Not authenticated')
      }

      // Get user's church ID from their profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', user.id)
        .single()

      if (profileError) {
        console.error('Profile error:', profileError)
        throw new Error(`Failed to load profile: ${profileError.message}`)
      }

      if (!profile || !profile.church_id) {
        console.error('Profile data:', profile)
        throw new Error('Your profile does not have a church assigned. Please contact your administrator.')
      }

      // Load church data
      const { data: churchData, error } = await supabase
        .from('churches')
        .select('*')
        .eq('id', profile.church_id)
        .single()

      if (error) {
        console.error('Church load error:', error)
        throw new Error(`Failed to load church: ${error.message}`)
      }

      if (!churchData) {
        throw new Error(`Church not found with ID: ${profile.church_id}`)
      }

      setChurch(churchData)
      if (churchData.logo_url) {
        setLogoPreview(churchData.logo_url)
      }
      if (churchData.banner_url) {
        setBannerPreview(churchData.banner_url)
      }
    } catch (error: any) {
      console.error('Error loading church settings:', error)
      setMessage({
        type: 'error',
        text: error.message || 'Failed to load church settings. Please check console for details.'
      })
    } finally {
      setLoading(false)
    }
  }

  async function handleLogoUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file || !church) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' })
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 2MB' })
      return
    }

    try {
      setUploadingLogo(true)
      setMessage(null)

      // Create unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${church.id}-logo-${Date.now()}.${fileExt}`
      const filePath = `church-logos/${fileName}`

      // Upload to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('public')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath)

      // Update church record
      const { error: updateError } = await supabase
        .from('churches')
        .update({ logo_url: publicUrl })
        .eq('id', church.id)

      if (updateError) throw updateError

      // Update local state
      setChurch({ ...church, logo_url: publicUrl })
      setLogoPreview(publicUrl)
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' })

      // Trigger page reload to update logo in sidebar
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error: any) {
      console.error('Error uploading logo:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to upload logo' })
    } finally {
      setUploadingLogo(false)
    }
  }

  async function handleBannerUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file || !church) return

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' })
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' })
      return
    }

    try {
      setUploadingBanner(true)
      setMessage(null)

      const fileExt = file.name.split('.').pop()
      const fileName = `${church.id}-banner-${Date.now()}.${fileExt}`
      const filePath = `church-banners/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath)

      const { error: updateError } = await supabase
        .from('churches')
        .update({ banner_url: publicUrl })
        .eq('id', church.id)

      if (updateError) throw updateError

      setChurch({ ...church, banner_url: publicUrl })
      setBannerPreview(publicUrl)
      setMessage({ type: 'success', text: 'Banner uploaded successfully!' })
    } catch (error: any) {
      console.error('Error uploading banner:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to upload banner' })
    } finally {
      setUploadingBanner(false)
    }
  }

  async function handleRemoveLogo() {
    if (!church || !confirm('Are you sure you want to remove the church logo?')) return

    try {
      setUploadingLogo(true)

      const { error } = await supabase
        .from('churches')
        .update({ logo_url: null })
        .eq('id', church.id)

      if (error) throw error

      setChurch({ ...church, logo_url: undefined })
      setLogoPreview(null)
      setMessage({ type: 'success', text: 'Logo removed successfully' })

      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to remove logo' })
    } finally {
      setUploadingLogo(false)
    }
  }

  async function handleSaveChurchInfo(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!church) return

    try {
      setSaving(true)
      setMessage(null)

      const formData = new FormData(e.currentTarget)
      const updates = {
        name: formData.get('name') as string,
        display_name: formData.get('display_name') as string,
        denomination: formData.get('denomination') as string,
        address: formData.get('address') as string,
        phone: formData.get('phone') as string,
        email: formData.get('email') as string,
        website: formData.get('website') as string,
        pastor_name: formData.get('pastor_name') as string,
        timezone: formData.get('timezone') as string,
        founded_date: formData.get('founded_date') as string,
      }

      const { error } = await supabase
        .from('churches')
        .update(updates)
        .eq('id', church.id)

      if (error) throw error

      setChurch({ ...church, ...updates })
      setMessage({ type: 'success', text: 'Church information saved successfully!' })
    } catch (error: any) {
      console.error('Error saving church info:', error)
      setMessage({ type: 'error', text: 'Failed to save church information' })
    } finally {
      setSaving(false)
    }
  }

  async function handleSavePreferences(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!church) return

    try {
      setSaving(true)
      setMessage(null)

      const formData = new FormData(e.currentTarget)

      // Build settings object from form data
      const settings = {
        ...church.settings,
        // Regional & Localization
        currency: formData.get('currency') as string,
        language: formData.get('language') as string,
        date_format: formData.get('date_format') as string,
        country: formData.get('country') as string,
        // Financial Settings
        fiscal_year_start: formData.get('fiscal_year_start') as string,
        tax_id: formData.get('tax_id') as string,
        enable_multi_currency: formData.get('enable_multi_currency') === 'on',
        show_usd_equivalent: formData.get('show_usd_equivalent') === 'on',
        // Communication
        sms_provider: formData.get('sms_provider') as string,
        email_from_name: formData.get('email_from_name') as string,
        auto_send_receipts: formData.get('auto_send_receipts') === 'on',
        notify_large_donations: formData.get('notify_large_donations') === 'on',
        large_donation_threshold: parseInt(formData.get('large_donation_threshold') as string) || 1000,
        // System Defaults
        member_number_format: formData.get('member_number_format') as string,
        member_number_prefix: formData.get('member_number_prefix') as string,
        attendance_grace_period: parseInt(formData.get('attendance_grace_period') as string) || 15,
        enable_qr_checkin: formData.get('enable_qr_checkin') === 'on',
      }

      const { error } = await supabase
        .from('churches')
        .update({ settings })
        .eq('id', church.id)

      if (error) throw error

      setChurch({ ...church, settings })
      setMessage({ type: 'success', text: 'Preferences saved successfully!' })

      // If language changed, apply immediately
      if (settings.language !== church.settings?.language) {
        await i18n.changeLanguage(settings.language)
        setMessage({
          type: 'success',
          text: 'Language updated! The interface will now display in ' + settings.language.toUpperCase()
        })
      }

      // If currency changed, suggest page reload
      if (settings.currency !== church.settings?.currency) {
        setMessage({
          type: 'success',
          text: 'Currency updated! Page will reload to apply changes...'
        })
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    } catch (error: any) {
      console.error('Error saving preferences:', error)
      setMessage({
        type: 'error',
        text: error.message || 'Failed to save preferences. Please check console for details.'
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!church) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load church settings</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Church Settings</h1>
        <p className="text-muted-foreground">
          Manage your church profile, logo, and preferences
        </p>
      </div>

      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="branding" className="space-y-4">
        <TabsList>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="information">Church Information</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Church Logo</CardTitle>
              <CardDescription>
                Upload your church logo. This will appear in the sidebar, reports, and official documents.
                <br />
                Recommended: Square image (400x400px), PNG or JPG, max 2MB
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {logoPreview ? (
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Church Logo"
                      className="w-32 h-32 object-contain border rounded-lg"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        variant="outline"
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Change Logo
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                        variant="destructive"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Current logo will appear on all PDF reports and in the admin sidebar
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Church className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-4">
                    No logo uploaded yet
                  </p>
                  <Button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </>
                    )}
                  </Button>
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Church Banner</CardTitle>
              <CardDescription>
                Optional banner image for website and promotional materials.
                <br />
                Recommended: 1200x400px, PNG or JPG, max 5MB
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {bannerPreview ? (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={bannerPreview}
                      alt="Church Banner"
                      className="w-full h-48 object-cover border rounded-lg"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={uploadingBanner}
                      variant="outline"
                    >
                      {uploadingBanner ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Change Banner
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-4">
                    No banner uploaded yet
                  </p>
                  <Button
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                  >
                    {uploadingBanner ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Banner
                      </>
                    )}
                  </Button>
                </div>
              )}
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerUpload}
                className="hidden"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Church Information Tab */}
        <TabsContent value="information">
          <Card>
            <CardHeader>
              <CardTitle>Church Information</CardTitle>
              <CardDescription>
                Update your church's basic information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveChurchInfo} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Church Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      defaultValue={church.name}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="display_name">Display Name</Label>
                    <Input
                      id="display_name"
                      name="display_name"
                      defaultValue={church.display_name || ''}
                      placeholder="Short name for UI"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="denomination">Denomination</Label>
                    <Input
                      id="denomination"
                      name="denomination"
                      defaultValue={church.denomination || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pastor_name">Pastor Name</Label>
                    <Input
                      id="pastor_name"
                      name="pastor_name"
                      defaultValue={church.pastor_name || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      defaultValue={church.phone || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      defaultValue={church.email || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      defaultValue={church.website || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="founded_date">Founded Date</Label>
                    <Input
                      id="founded_date"
                      name="founded_date"
                      type="date"
                      defaultValue={church.founded_date || ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Input
                      id="timezone"
                      name="timezone"
                      defaultValue={church.timezone || 'Africa/Accra'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    name="address"
                    defaultValue={church.address || ''}
                    rows={3}
                  />
                </div>

                <Separator />

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <div className="space-y-4">
            {/* Regional & Localization */}
            <Card>
              <CardHeader>
                <CardTitle>Regional & Localization</CardTitle>
                <CardDescription>
                  Currency, language, and regional settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePreferences} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Currency Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="currency">Default Currency *</Label>
                      <select
                        id="currency"
                        name="currency"
                        defaultValue={church?.settings?.currency || 'GHS'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="GHS">🇬🇭 GHS - Ghana Cedi</option>
                        <option value="NGN">🇳🇬 NGN - Nigerian Naira</option>
                        <option value="KES">🇰🇪 KES - Kenyan Shilling</option>
                        <option value="UGX">🇺🇬 UGX - Ugandan Shilling</option>
                        <option value="TZS">🇹🇿 TZS - Tanzanian Shilling</option>
                        <option value="ZAR">🇿🇦 ZAR - South African Rand</option>
                        <option value="USD">🇺🇸 USD - US Dollar</option>
                        <option value="EUR">🇪🇺 EUR - Euro</option>
                        <option value="GBP">🇬🇧 GBP - British Pound</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Used for all financial displays and reports
                      </p>
                    </div>

                    {/* Language - HIDDEN UNTIL PHASE 2 UI TRANSLATION IS COMPLETE */}
                    <div className="space-y-2" style={{ display: 'none' }}>
                      <Label htmlFor="language">Language</Label>
                      <select
                        id="language"
                        name="language"
                        defaultValue={church?.settings?.language || 'en'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.flag} {lang.name} ({lang.nativeName})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Choose your preferred language. The system will display in this language after you save.
                      </p>
                    </div>

                    {/* Date Format */}
                    <div className="space-y-2">
                      <Label htmlFor="date_format">Date Format</Label>
                      <select
                        id="date_format"
                        name="date_format"
                        defaultValue={church?.settings?.date_format || 'DD/MM/YYYY'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                      </select>
                    </div>

                    {/* Country */}
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <select
                        id="country"
                        name="country"
                        defaultValue={church?.settings?.country || 'GH'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="GH">🇬🇭 Ghana</option>
                        <option value="NG">🇳🇬 Nigeria</option>
                        <option value="KE">🇰🇪 Kenya</option>
                        <option value="UG">🇺🇬 Uganda</option>
                        <option value="TZ">🇹🇿 Tanzania</option>
                        <option value="ZA">🇿🇦 South Africa</option>
                        <option value="US">🇺🇸 United States</option>
                        <option value="GB">🇬🇧 United Kingdom</option>
                      </select>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Preferences
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Financial Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Settings</CardTitle>
                <CardDescription>
                  Accounting and financial management preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePreferences} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fiscal Year Start */}
                    <div className="space-y-2">
                      <Label htmlFor="fiscal_year_start">Fiscal Year Start</Label>
                      <select
                        id="fiscal_year_start"
                        name="fiscal_year_start"
                        defaultValue={church?.settings?.fiscal_year_start || 'January'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="January">January (Calendar Year)</option>
                        <option value="April">April</option>
                        <option value="July">July</option>
                        <option value="October">October</option>
                      </select>
                    </div>

                    {/* Tax ID */}
                    <div className="space-y-2">
                      <Label htmlFor="tax_id">Tax ID / Registration Number</Label>
                      <Input
                        id="tax_id"
                        name="tax_id"
                        defaultValue={church?.settings?.tax_id || ''}
                        placeholder="e.g., C0123456789"
                      />
                    </div>

                    {/* Enable Multi-Currency */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          id="enable_multi_currency"
                          name="enable_multi_currency"
                          type="checkbox"
                          defaultChecked={church?.settings?.enable_multi_currency || false}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="enable_multi_currency" className="cursor-pointer">
                          Enable Multi-Currency Support
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Allow contributions in multiple currencies
                      </p>
                    </div>

                    {/* Show USD Equivalent */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          id="show_usd_equivalent"
                          name="show_usd_equivalent"
                          type="checkbox"
                          defaultChecked={church?.settings?.show_usd_equivalent || false}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="show_usd_equivalent" className="cursor-pointer">
                          Show USD Equivalent
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Display approximate USD value alongside amounts
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Financial Settings
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Communication Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Communication Preferences</CardTitle>
                <CardDescription>
                  SMS, email, and notification settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePreferences} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SMS Provider */}
                    <div className="space-y-2">
                      <Label htmlFor="sms_provider">Preferred SMS Provider</Label>
                      <select
                        id="sms_provider"
                        name="sms_provider"
                        defaultValue={church?.settings?.sms_provider || 'mnotify'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="mnotify">mNotify (Ghana)</option>
                        <option value="africastalking">Africa's Talking (East Africa)</option>
                        <option value="twilio">Twilio (Global)</option>
                        <option value="none">None (Disabled)</option>
                      </select>
                    </div>

                    {/* Email From Name */}
                    <div className="space-y-2">
                      <Label htmlFor="email_from_name">Email From Name</Label>
                      <Input
                        id="email_from_name"
                        name="email_from_name"
                        defaultValue={church?.settings?.email_from_name || church?.name || ''}
                        placeholder="Church Name"
                      />
                    </div>

                    {/* Auto Send Receipts */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          id="auto_send_receipts"
                          name="auto_send_receipts"
                          type="checkbox"
                          defaultChecked={church?.settings?.auto_send_receipts !== false}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="auto_send_receipts" className="cursor-pointer">
                          Auto-send contribution receipts
                        </Label>
                      </div>
                    </div>

                    {/* SMS Notifications for Large Donations */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          id="notify_large_donations"
                          name="notify_large_donations"
                          type="checkbox"
                          defaultChecked={church?.settings?.notify_large_donations || false}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="notify_large_donations" className="cursor-pointer">
                          Notify admins of large donations
                        </Label>
                      </div>
                      <Input
                        type="number"
                        name="large_donation_threshold"
                        defaultValue={church?.settings?.large_donation_threshold || 1000}
                        placeholder="Threshold amount"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Communication Settings
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Demo Mode */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Beaker className="w-5 h-5" />
                  Demo Mode
                </CardTitle>
                <CardDescription>
                  Enable demo mode to display sample data for demonstrations and marketing purposes.
                  This is useful for showcasing features without using real church data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <DemoModeToggle showLabel={true} />
                  <p className="text-sm text-muted-foreground">
                    When enabled, the dashboard and other pages will display sample data instead of actual church data.
                    Demo data includes sample members, events, announcements, and financial information.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* System Defaults */}
            <Card>
              <CardHeader>
                <CardTitle>System Defaults</CardTitle>
                <CardDescription>
                  Default settings for various features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePreferences} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Member Number Format */}
                    <div className="space-y-2">
                      <Label htmlFor="member_number_format">Member Number Format</Label>
                      <select
                        id="member_number_format"
                        name="member_number_format"
                        defaultValue={church?.settings?.member_number_format || 'prefix_sequential'}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="sequential">Sequential (001, 002, 003...)</option>
                        <option value="prefix_sequential">Prefix + Sequential (MEM-001, MEM-002...)</option>
                        <option value="year_sequential">Year + Sequential (2024-001, 2024-002...)</option>
                      </select>
                    </div>

                    {/* Member Number Prefix */}
                    <div className="space-y-2">
                      <Label htmlFor="member_number_prefix">Member Number Prefix</Label>
                      <Input
                        id="member_number_prefix"
                        name="member_number_prefix"
                        defaultValue={church?.settings?.member_number_prefix || 'MEM'}
                        placeholder="e.g., MEM, MEMBER"
                        maxLength={10}
                      />
                    </div>

                    {/* Attendance Grace Period */}
                    <div className="space-y-2">
                      <Label htmlFor="attendance_grace_period">Attendance Grace Period (minutes)</Label>
                      <Input
                        id="attendance_grace_period"
                        name="attendance_grace_period"
                        type="number"
                        defaultValue={church?.settings?.attendance_grace_period || 15}
                        min="0"
                        max="60"
                      />
                      <p className="text-xs text-muted-foreground">
                        Allow check-in this many minutes after service starts
                      </p>
                    </div>

                    {/* Enable QR Code Check-in */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          id="enable_qr_checkin"
                          name="enable_qr_checkin"
                          type="checkbox"
                          defaultChecked={church?.settings?.enable_qr_checkin !== false}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="enable_qr_checkin" className="cursor-pointer">
                          Enable QR Code Check-in
                        </Label>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save System Defaults
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
